import React, { useState } from 'react';
import { Button, Flex } from 'antd';
import { CustomDeleteModal } from '@/pages/Modals';
import {
  SavedDateList,
  SavedDateListInUseError,
  SavedDateListValidationError,
  savedListErrorMessage,
} from '@/apis/logiblocksAPI';
import SavedListPanelShell from './SavedListPanelShell';
import ListNameField from './ListNameField';
import DateSetEntriesSection from './DateSetEntriesSection';
import LogiblocksDatePickerPanel from './LogiblocksDatePickerPanel';
import DiscardConfirmPrompt from './DiscardConfirmPrompt';
import PrimaryButton from './PrimaryButton';
import {
  useDeleteSavedDateList,
  useUpdateSavedDateList,
} from './dateSetQueries';
import {
  dateSetValueToSavedListInput,
  savedListToDateSetValue,
} from './dateSetUtils';
import styles from './SavedListPanel.module.css';

interface ListEditFormProps {
  list: SavedDateList;
  onBack: () => void;
  onUpdated: (list: SavedDateList) => void;
  onDeleted: () => void;
}

function usageText(usedInCount: number): string {
  return `Used in ${usedInCount} automation${usedInCount !== 1 ? 's' : ''}.`;
}

/** The edit form for a loaded saved list: name, usage, and chips, with
 * Apply as the single commit for name/entries/timezone. Clicking "Add" drills
 * into the shared calendar panel, which returns without committing. Seeded
 * from `list` once; later refetches don't overwrite the user's edits. */
const ListEditForm: React.FC<ListEditFormProps> = ({
  list,
  onBack,
  onUpdated,
  onDeleted,
}) => {
  const [initialValue] = useState(() => savedListToDateSetValue(list));
  const [name, setName] = useState(list.name);
  const [value, setValue] = useState(initialValue);
  const [showCalendar, setShowCalendar] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const updateList = useUpdateSavedDateList(list.id);
  const deleteList = useDeleteSavedDateList(list.id);

  const isDirty =
    name.trim() !== list.name ||
    JSON.stringify(value) !== JSON.stringify(initialValue);
  const count = value.entries.entries.length;
  const usedInCount = list.used_in_count;

  const requestBack = () => {
    if (isDirty) {
      setShowDiscardConfirm(true);
    } else {
      onBack();
    }
  };

  const handleApply = async () => {
    setError(null);
    try {
      const updated = await updateList.mutateAsync(
        dateSetValueToSavedListInput(name.trim(), value)
      );
      onUpdated(updated);
    } catch (e) {
      if (!(e instanceof SavedDateListValidationError)) console.error(e);
      setError(savedListErrorMessage(e, 'Failed to update list'));
    }
  };

  const handleDelete = async () => {
    setDeleteError(null);
    try {
      await deleteList.mutateAsync();
      onDeleted();
    } catch (e) {
      if (!(e instanceof SavedDateListInUseError)) console.error(e);
      setDeleteError(savedListErrorMessage(e, 'Failed to delete list'));
    } finally {
      setShowDeleteConfirm(false);
    }
  };

  if (showCalendar) {
    return (
      <LogiblocksDatePickerPanel
        value={value}
        footerVerb='Done'
        onSubmit={(next) => {
          setValue(next);
          setShowCalendar(false);
        }}
      />
    );
  }

  return (
    <SavedListPanelShell title={list.name} onBack={requestBack}>
      {showDiscardConfirm && (
        <DiscardConfirmPrompt
          onKeepEditing={() => setShowDiscardConfirm(false)}
          onDiscard={onBack}
        />
      )}

      <ListNameField
        value={name}
        onChange={(next) => {
          setName(next);
          setError(null);
        }}
        error={error}
      />

      <div className={styles.usage}>
        {usedInCount > 0 ? (
          <>
            Used in{' '}
            <span
              className={styles.usageCount}
              title={list.used_in_workflows
                .map((workflow) => workflow.name || 'Untitled automation')
                .join('\n')}
            >
              {usedInCount} automation{usedInCount !== 1 ? 's' : ''}
            </span>
            . Changes apply on their next run.
          </>
        ) : (
          'Not used in any automation.'
        )}
      </div>

      <DateSetEntriesSection value={value} onChange={setValue} />

      <button
        type='button'
        className={styles.addMore}
        onClick={() => setShowCalendar(true)}
      >
        + Add {value.entries.kind === 'dates' ? 'dates' : 'days'}
      </button>

      <Flex vertical gap={4} className={styles.footer}>
        <Flex justify='flex-end' align='center' gap={8}>
          <Button
            danger
            type='text'
            disabled={usedInCount > 0}
            onClick={() => setShowDeleteConfirm(true)}
            title={
              usedInCount > 0
                ? `${usageText(usedInCount)} Replace it in those conditions before removing.`
                : undefined
            }
          >
            Remove
          </Button>
          <Button onClick={requestBack}>Cancel</Button>
          <PrimaryButton
            disabled={
              !name.trim() || count === 0 || updateList.isPending || !isDirty
            }
            loading={updateList.isPending}
            onClick={handleApply}
          >
            Apply
          </PrimaryButton>
        </Flex>
        {deleteError && <span className={styles.error}>{deleteError}</span>}
      </Flex>

      <CustomDeleteModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={`Remove ${list.name}?`}
        message='This cannot be undone.'
        cancelText='Cancel'
        confirmText='Remove'
        confirmColor='var(--danger)'
        disableConfirmBtn={deleteList.isPending}
      />
    </SavedListPanelShell>
  );
};

export default ListEditForm;
